import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks'
import { fetchBoxTruckLoadsAccess } from '../services/boxTruckLoadsApi'
import { setAccountantDefault, setBoxTruckCompany, setIsOperator, setPricingAccess, setProfilesFromRole } from '../store/boxTruckLoadsSlice'

/** Last CONFIRMED access answer, scoped PER ACCOUNT AND COMPANY — seeds the section instantly on the next
 *  session so a slow or briefly-down backend can't blank the menu for an operator who was allowed a minute
 *  ago. Account scoping means a shared browser never seeds account B's UI with account A's answer; the
 *  company half matters because BOTH answers are per-membership — workspace access is inherited from the
 *  company, and the Profiles grant turns on whether THAT company runs Box Truck. One Multi-MC user holds
 *  one standalone id across several of them, so a key without the company seeded a switched tab from the
 *  membership it just left, and a failing request preserves a seeded answer indefinitely by design. */
const cacheKey = (userId: string, applicationId: string): string => `boxTruckLoads:lastAccess:${userId}:${applicationId}`

/** One-shot checks were the flakiness: a single failed request (backend restarting, token refresh race)
 *  hid the section for the WHOLE session. Retry this many times with exponential backoff instead. */
const MAX_RETRIES = 5

/**
 * Resolves whether the logged-in account may see Box Truck Loads (the Gluon-managed allowlist) and
 * stores `isOperator` so the sidebar can hide the section for non-operators.
 *
 * Robustness rules (menu/route visibility only — every actual endpoint stays authorized server-side):
 *  - Seed from the last CONFIRMED answer for THIS account (localStorage) so the section renders
 *    immediately and survives a transient backend outage.
 *  - On a request error, RETRY with backoff; never flip visibility on an error. Only an explicit server
 *    "allowed: false" hides the section (and updates the cache).
 *  - With no cache and all retries failing, the state stays null → hidden (deny by default).
 */
export function useBoxTruckLoadsOperator(enabled: boolean): void {
    const dispatch = useAppDispatch()
    const userId = useAppSelector((state) => state.auth.user?.id)
    // In the dependency list as well as the key: a per-tab company switch keeps the same user and does not
    // reload, so without it the effect never re-ran and both answers stayed the previous company's.
    const applicationId = useAppSelector((state) => state.auth.application?.id)
    useEffect(() => {
        // Including when there is nobody to answer for. `logout` resets the auth slice and navigates
        // without reloading the store, so an answer left here would outlive the identity that earned it and
        // be the first thing the next sign-in in this tab renders from.
        if (!enabled) {
            dispatch(setIsOperator(null))
            dispatch(setProfilesFromRole(false))
            dispatch(setAccountantDefault(false))
            dispatch(setBoxTruckCompany(false))
            dispatch(setPricingAccess(false))
            return
        }
        let cancelled = false
        let timer: number | undefined
        // Seed from this membership's own answer, or go back to not knowing. The reset is the half that
        // matters on a per-tab company switch: the effect re-runs, but a company this browser has not seen
        // before has nothing to seed from, and whatever was left in the store is the PREVIOUS company's —
        // `isOperator === true` renders the Box Truck routes (`BoxTruckGuard`), so company A's answer would
        // let company B's tab into the section until the request lands, or for as long as it retries.
        //
        // Not the "never flip visibility on an error" rule this hook is built around: that rule is about a
        // failed answer for the SAME question. This is a different question being asked, and `null` is what
        // "not yet answered" already means here.
        let seeded = false
        if (userId && applicationId) {
            try {
                const cached = localStorage.getItem(cacheKey(userId, applicationId))
                if (cached) {
                    const { allowed, profilesFromRole, accountantDefault, boxTruckCompany, pricing } = JSON.parse(cached) as {
                        allowed?: boolean
                        profilesFromRole?: boolean
                        accountantDefault?: boolean
                        boxTruckCompany?: boolean
                        pricing?: boolean
                    }
                    dispatch(setIsOperator(!!allowed))
                    dispatch(setProfilesFromRole(!!profilesFromRole))
                    dispatch(setAccountantDefault(!!accountantDefault))
                    // Seeded false when absent, which is the safe direction: it only ever ADDS the
                    // read-only rule, so a stale cache without it behaves exactly as today until the live
                    // answer lands a moment later.
                    dispatch(setBoxTruckCompany(!!boxTruckCompany))
                    dispatch(setPricingAccess(!!pricing))
                    seeded = true
                }
            } catch {
                // Corrupt cache — ignore; the live check below is the source of truth.
            }
        }
        if (!seeded) {
            dispatch(setIsOperator(null))
            dispatch(setProfilesFromRole(false))
            dispatch(setAccountantDefault(false))
            dispatch(setBoxTruckCompany(false))
            dispatch(setPricingAccess(false))
        }
        let attempt = 0
        const check = () => {
            fetchBoxTruckLoadsAccess()
                .then(({ allowed, profilesFromRole, accountantDefault, boxTruckCompany, pricing }) => {
                    if (cancelled) return
                    dispatch(setIsOperator(allowed))
                    dispatch(setProfilesFromRole(!!profilesFromRole))
                    dispatch(setAccountantDefault(!!accountantDefault))
                    dispatch(setBoxTruckCompany(!!boxTruckCompany))
                    dispatch(setPricingAccess(!!pricing))
                    if (userId && applicationId) {
                        try {
                            localStorage.setItem(
                                cacheKey(userId, applicationId),
                                JSON.stringify({ allowed, profilesFromRole, accountantDefault, boxTruckCompany, pricing })
                            )
                        } catch {
                            // Quota/private-mode — fine, we just lose the fast seed next session.
                        }
                    }
                })
                .catch(() => {
                    if (cancelled) return
                    attempt += 1
                    if (attempt <= MAX_RETRIES) timer = window.setTimeout(check, Math.min(30_000, 1000 * 2 ** attempt))
                })
        }
        check()
        return () => {
            cancelled = true
            if (timer) window.clearTimeout(timer)
        }
    }, [enabled, dispatch, userId, applicationId])
}
